import React, { useCallback, useEffect, useState } from "react";
import { List, Typography, Avatar, Space, Flex, Modal } from "antd";
import {
  EditOutlined,
  LogoutOutlined,
  RightOutlined,
  UserAddOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import { BsMegaphone } from "react-icons/bs";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { RootState } from "../../../redux/store";
import {
  setCurrentChat,
  updateCurrentChat,
} from "../../../redux/slices/chatSlice";
import { useAppDispatch } from "../../../redux/utils/Dispatcher";
import { useChatSidebarContext } from "../../../context/ChatSidebarScreenContext";
import { formatChats } from "../../../utils";
import ENDPOINTS from "../../../services/endpoint";
import apiInstance from "../../../services/apiService";
import UpdateBroadCast from "./UpdateBroadCast";
import AddNewRecipient from "./AddNewRecipient";
import ChatSidebarHeader from "../../../components/common/ChatSidebarHeader";
import CustomLoader from "../../../components/common/UI/Loader";
import "./index.scss";

const { Title, Text } = Typography;
const MegaphoneIcon = BsMegaphone as unknown as React.FC;

const BroadCastInfo = () => {
  // in future sync with mobile will be there so keep it commented
  //   const { socket } = useSocket();
  const dispatch = useAppDispatch();
  const { currentChat } = useSelector((state: RootState) => state.chat);
  const { contacts } = useSelector((state: RootState) => state.contacts);
  const { setCurrentScreen } = useChatSidebarContext();
  const [currentBroadcastScreen, setCurrentBroadcastScreen] = useState("info");
  const [broadCastInfo, setBroadCastInfo] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [removingRecipient, setRemovingRecipient] = useState<boolean>(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);
  const [leavingGroup, setLeavingGroup] = useState<boolean>(false);

  const handleBackToInfo = () => {
    setCurrentBroadcastScreen("info");
  };

  const addContactNames = (conversation: any) => {
    const membersWithSavedNames = conversation[0]?.broadCastMembers.map(
      (member: any) => {
        const matchedContact = contacts?.find(
          (contact: any) => contact?.phoneNumber === member?.phoneNumber
        );

        if (matchedContact) {
          return { ...member, savedName: matchedContact?.fullName };
        } else {
          return { ...member, savedName: "" };
        }
      }
    );

    const updatedListWithSavedNames = {
      ...conversation[0],
      broadCastMembers: membersWithSavedNames,
    };

    return updatedListWithSavedNames;
  };

  const fetchBroadCastInfo = useCallback(async () => {
    if (!currentChat?.id) return;
    const URL = ENDPOINTS.CONVERSATION_LIST + `?cId=${currentChat?.id}`;

    setLoading(true);
    try {
      const response = await apiInstance.get(URL);
      if (response.status === 200) {
        if (currentChat?.isArchived) {
          const conversation = formatChats(
            response?.data?.newCnv?.docs?.[0]?.archivedList
          );

          setBroadCastInfo(addContactNames(conversation));
          return conversation;
        } else {
          const conversation = formatChats(
            response?.data?.newCnv?.docs?.[0]?.conversationList
          );

          setBroadCastInfo(addContactNames(conversation));
          return conversation;
        }
      }
    } catch (err: any) {
      console.error("Error fetching conversations:", err);
      toast.error(err.message || "There was an error fetching Conversations");
    } finally {
      setLoading(false);
    }
  }, [currentChat]);

  const handleDelete = async () => {
    const URL = ENDPOINTS.DELETE_BROADCAST + `?chatId=${broadCastInfo?.id}`;

    setLeavingGroup(true);
    try {
      const response = await apiInstance.delete(URL);

      if (response.status === 200) {
        dispatch(setCurrentChat(null));
        setCurrentScreen("chats");
      }
    } catch (err: any) {
      console.error("Error removing member:", err);
      toast.error(err.message || "There was an error. Please try again");
    } finally {
      setLeavingGroup(false);
      setDeleteModalOpen(false);
    }
  };

  const handleRemove = async (member: any) => {
    const URL = ENDPOINTS.UPDATE_BROADCAST;
    setRemovingRecipient(true);

    try {
      const response = await apiInstance.post(URL, {
        chatId: broadCastInfo?.id,
        removeUserId: member.phoneNumber,
      });

      if (response.status === 200) {
        const updatedRecipients = await fetchBroadCastInfo();
        dispatch(
          updateCurrentChat({
            broadCastMembers: updatedRecipients?.broadCastMembers,
          })
        );
      }
    } catch (err: any) {
      toast.error(err.message || "Error creating group. Please try again");
    } finally {
      setRemovingRecipient(false);
    }
  };

  useEffect(() => {
    fetchBroadCastInfo();
  }, [currentBroadcastScreen, fetchBroadCastInfo]);

  return (
    <>
      {currentBroadcastScreen === "info" && (
        <div className="broadcast-info-wrapper">
          <ChatSidebarHeader
            title="List Info"
            disabled={loading}
            handleBackClick={() => {
              setCurrentScreen(currentChat?.previousScreen || "chats");
            }}
          />

          <div className="main">
            {loading ? (
              <Flex className="w-full" justify="center">
                <CustomLoader />
              </Flex>
            ) : (
              <>
                <div className={`group-info-card`}>
                  <Flex
                    className="w-full"
                    align="center"
                    justify="space-between"
                  >
                    <Flex align="center" gap={8}>
                      <div className="avatar">
                        <MegaphoneIcon />
                      </div>
                      <Space direction="vertical" size={0}>
                        <Title className="card-title">
                          {broadCastInfo?.name}
                        </Title>
                        <Text className="group-date">
                          Created on {broadCastInfo?.date}
                        </Text>
                      </Space>
                    </Flex>

                    <EditOutlined
                      className="icon-edit cursor-pointer"
                      onClick={() => {
                        setCurrentBroadcastScreen("update-group-info");
                      }}
                    />
                  </Flex>
                </div>

                <Text className="recipients">
                  RECIPIENTS : {broadCastInfo?.broadCastMembers?.length}
                </Text>

                <div className="group-section">
                  <Flex
                    className={`w-full cursor-pointer `}
                    align="center"
                    justify="space-between"
                    onClick={() => {
                      setCurrentBroadcastScreen("add-new-member");
                    }}
                  >
                    <Flex align="center" gap={8}>
                      <Avatar className="avatar" size={45}>
                        <UserAddOutlined />
                      </Avatar>
                      <Title className="card-title">Add Recipients</Title>
                    </Flex>
                    <RightOutlined className="right-icon" />
                  </Flex>

                  <List
                    className="members-list"
                    dataSource={broadCastInfo?.broadCastMembers || []}
                    renderItem={(member: any) => (
                      <Flex
                        className="w-full"
                        align="center"
                        justify="space-between"
                      >
                        <Flex align="center" gap={8}>
                          <Avatar className="avatar" size={45}>
                            <UserAddOutlined />
                          </Avatar>
                          <Flex vertical className="card-title">
                            {member?.savedName
                              ? member?.savedName
                              : member?.phoneNumber}

                            {member?.savedName && (
                              <Text className="card-sub-title">
                                {member?.phoneNumber}
                              </Text>
                            )}
                          </Flex>
                        </Flex>
                        <div className="remove-user-icon">
                          <DeleteOutlined
                            disabled={removingRecipient}
                            onClick={() => handleRemove(member)}
                          />
                        </div>
                      </Flex>
                    )}
                  />
                </div>

                <div
                  onClick={() => setDeleteModalOpen(true)}
                  className={`group-info-card cursor-pointer`}
                >
                  <Flex className="h-full" align="center" gap={8}>
                    <LogoutOutlined className="exit-icon" />
                    <Title className="card-title">Delete Broadcast</Title>
                  </Flex>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {currentBroadcastScreen === "update-group-info" && (
        <UpdateBroadCast
          broadCastInfo={broadCastInfo}
          handleBackClick={handleBackToInfo}
        />
      )}

      {currentBroadcastScreen === "add-new-member" && (
        <AddNewRecipient
          broadCastInfo={broadCastInfo}
          handleBackClick={handleBackToInfo}
        />
      )}

      <Modal
        className="custom-modal"
        centered
        title="Confirm Delete"
        open={deleteModalOpen}
        onCancel={() => setDeleteModalOpen(false)}
        okButtonProps={{
          loading: leavingGroup,
          className: "modal-ok-btn",
        }}
        cancelButtonProps={{
          className: "modal-cancel-btn",
        }}
        onOk={() => {
          handleDelete();
        }}
      >
        <Text className="text-primary">
          Are you sure you want to delete this Broadcast?
        </Text>
      </Modal>
    </>
  );
};

export default BroadCastInfo;
